import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useAtomValue, useStore } from 'jotai';
import type { ExportImageOptions } from 'components/chart-export';
import { useEval3Page } from './context';
import { useExportLifecycleToast } from 'lib/hooks/useExportLifecycleToast';
import {
  cashflowsVisibleColumnsAtom,
  isChartsVisualsRenderedAtom,
  isMapRenderedAtom,
  mapScopeAtom,
  masterDataVisibleColumnsAtom,
  rollupBinAtom,
  rollupCashflowPeriodAtom,
  rollupDimensionsAtom,
  serverFiltersAtom,
} from './atoms';
import {
  useChartExportRegistry,
  type ChartExportGroup,
} from './chart-export-registry';
import { useContentExport, useRollupOptions } from './hooks';
import {
  CONTENT_EXPORT_ITEMS,
  DEFAULT_COLUMN_PRESET,
  defaultContentExportImageOptions,
  defaultSpatialConfig,
  COLUMN_PRESET_SECTIONS,
  rollupBinParams,
  rollupHasGrouping,
  type ContentExportItemId,
  type ColumnPresetSection,
} from './utils';
import type {
  ContentExportPayload,
  ContentExportSection,
  ColumnPreset,
  MasterDataView,
  SpatialExportConfig,
} from './types';
import type { ExportContentModalProps } from './export-content-modal';

/**
 * Maps each preset section to its master-data view (for reading the view-keyed visibility atom),
 * or `null` for cashflows (which has its own atom, not a master-data view). Keyed over the full
 * `ColumnPresetSection` so the type — not statement order — enforces that every section is routed.
 */
const PRESET_SECTION_TO_VIEW: Record<
  ColumnPresetSection,
  MasterDataView | null
> = {
  slots: 'slots',
  slot_allocations: 'allocations',
  cashflows_by_month: null,
};

/**
 * The initial per-table column presets — every table defaults to {@link DEFAULT_COLUMN_PRESET}
 * (Finance Team Format).
 *
 * @return {Record<ColumnPresetSection, ColumnPreset>} The default preset map.
 */
const defaultColumnPresets = (): Record<ColumnPresetSection, ColumnPreset> => ({
  slots: DEFAULT_COLUMN_PRESET,
  slot_allocations: DEFAULT_COLUMN_PRESET,
  cashflows_by_month: DEFAULT_COLUMN_PRESET,
});

/** Items disabled in package/workspace mode (no eval). */
const PACKAGE_MODE_DISABLED_IDS: ContentExportItemId[] = [
  'evaluation_output_aggregation',
  'reserves_evolution',
  'production_volumes',
  'free_cash_flow',
  'cashflows_by_month',
  'charts_visuals',
];

/** What {@link useContentExportFlow} returns — mirrors {@link useAriesMdbExportFlow}. */
export interface ContentExportFlow {
  /** Opens the modal fresh (all unchecked, default Image Options + spatial config, prior status cleared). */
  open: () => void;
  /**
   * True while an export is in flight — capturing/uploading AND the post-202 Pusher wait — so the
   * menu item can disable, matching the old standalone Export button (no second kickoff mid-run).
   */
  isExporting: boolean;
  /** Ready-to-spread props for `<ExportContentModal>`. */
  modalProps: ExportContentModalProps;
  /** The transient success/error toast node (self-dismissing), or null when idle. */
  toast: ReactNode;
}

/**
 * Encapsulates the Content-Export feature (P8 · Slice 1) behind one hook — the modal's data +
 * the async {@link useContentExport} engine — so the header **Actions** menu (`ActionsMenu`)
 * stays a thin container: it renders the trigger (`open`) as a menu item and spreads
 * `modalProps` onto {@link ExportContentModal}, with no export state of its own. Mirrors
 * {@link useAriesMdbExportFlow}.
 *
 * Supports two modes:
 *   - **Eval mode** (calculationId present) — all sections available.
 *   - **Package mode** (calculationId null) — eval-only sections disabled; spatial, map,
 *     and master-data sections export against the live package population.
 *
 * It reads the live page state the export payload needs straight from the module atoms
 * (`serverFiltersAtom`, `mapScopeAtom`, `rollupDimensionsAtom`, `rollupCashflowPeriodAtom`) +
 * the ValueRollup options default (for the effective group-by) + the chart-export registry
 * (the rendered charts to capture), assembles the payload at Export-click time, and drives
 * the modal open/close + checked set + Image Options. Because the async engine is owned HERE
 * (not in the modal), closing the modal never aborts an in-flight export — the completion
 * toast + download still land (P8 background-safe).
 *
 * @return {ContentExportFlow} The menu trigger, in-flight flag, modal props, and toast node.
 */
export function useContentExportFlow(): ContentExportFlow {
  const { calculationId } = useEval3Page();
  const isPackageMode = calculationId === null;
  // The payload atoms (filters / mapScope / dimensions / period) are read imperatively at
  // Export-click time via the store — NOT subscribed — so page-state churn (a filter edit, a
  // map-scope change) never re-renders this always-mounted title-bar owner.
  const store = useStore();
  // These "is rendered" flags ARE read in render (they drive the disabled checkboxes + the
  // reconcile effect below), so they stay subscriptions.
  const chartsVisualsRendered = useAtomValue(isChartsVisualsRenderedAtom);
  const mapRendered = useAtomValue(isMapRenderedAtom);
  const { getEntries } = useChartExportRegistry();
  const { status, error, isExporting, start, reset } = useContentExport();

  const [open, setOpen] = useState(false);
  const [checkedIds, setCheckedIds] = useState<Set<ContentExportItemId>>(
    () => new Set()
  );
  const [imageOptions, setImageOptions] = useState<ExportImageOptions>(() =>
    defaultContentExportImageOptions()
  );
  const [spatialConfig, setSpatialConfig] =
    useState<SpatialExportConfig>(defaultSpatialConfig);
  // Per-table column preset for Properties / Property Allocations / Cashflows by Month (Assets
  // always exports all columns). Read at Export-click and folded into `config.<section>`.
  const [columnPresets, setColumnPresets] = useState(defaultColumnPresets);

  // The effective group-by fallback is only needed at Export-click, so fetch it lazily on
  // modal open (a static, session-stable catalog — cached after the first open, and already
  // cached whenever the ValueRollup section is shown). Avoids an eager fetch on every load.
  const { data: rollupOptions } = useRollupOptions({ enabled: open });

  // WYSIWYG image items are disablable — nothing to capture when their surface isn't rendered.
  // Charts & Visuals: its Layout toggle is off or no chart is visible. Map: its Layout toggle
  // is off (the Mapbox canvas is 0-size / display:none, so it captures blank).
  // Package mode: eval-only sections (cashflows, rollup, rescat, charts) are disabled.
  const disabledIds = useMemo<Set<ContentExportItemId>>(() => {
    const set = new Set<ContentExportItemId>();
    if (!chartsVisualsRendered) {
      set.add('charts_visuals');
    }
    if (!mapRendered) {
      set.add('map');
    }
    if (isPackageMode) {
      PACKAGE_MODE_DISABLED_IDS.forEach((id) => set.add(id));
    }
    return set;
  }, [chartsVisualsRendered, mapRendered, isPackageMode]);

  // If an item becomes disabled while the modal is open (a Layout toggle flips, the last chart
  // is hidden), drop it from the checked set so a disabled item can never be exported.
  useEffect(() => {
    setCheckedIds((prev) => {
      const next = new Set([...prev].filter((id) => !disabledIds.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [disabledIds]);

  const openModal = () => {
    // Open fresh — all unchecked, default Image Options, default spatial config,
    // prior export status cleared (FR: Open).
    setCheckedIds(new Set());
    setImageOptions(defaultContentExportImageOptions());
    // Package mode forces 'package' basis with current datetime; eval mode defaults to eval_run.
    setSpatialConfig(
      isPackageMode
        ? {
            ...defaultSpatialConfig(),
            basis: 'package',
            as_of_date: new Date().toISOString().slice(0, 16),
          }
        : defaultSpatialConfig()
    );
    setColumnPresets(defaultColumnPresets());
    reset();
    setOpen(true);
  };

  const setColumnPreset = useCallback(
    (section: ColumnPresetSection, preset: ColumnPreset) => {
      setColumnPresets((prev) => ({ ...prev, [section]: preset }));
    },
    []
  );

  const toggleItem = useCallback(
    (id: ContentExportItemId, checked: boolean) => {
      setCheckedIds((prev) => {
        const next = new Set(prev);
        if (checked) {
          next.add(id);
        } else {
          next.delete(id);
        }
        return next;
      });
    },
    []
  );

  // Image Options appear when a checked item captures an image (any item with an
  // `imageGroup` — e.g. Charts & Visuals, Reserves, Output Aggregation, Production
  // Volumes, Map).
  const showImageOptions = useMemo(
    () =>
      CONTENT_EXPORT_ITEMS.some(
        (item) => item.imageGroup && checkedIds.has(item.id)
      ),
    [checkedIds]
  );

  const handleExport = () => {
    const sections: ContentExportSection[] = [];
    const imageGroups: ChartExportGroup[] = [];
    CONTENT_EXPORT_ITEMS.forEach((item) => {
      if (!checkedIds.has(item.id)) {
        return;
      }
      // An item can contribute both a data section AND an image (Reserves = table + chart).
      if (item.section) {
        sections.push(item.section);
      }
      if (item.imageGroup) {
        imageGroups.push(item.imageGroup);
      }
    });

    // Read the live page state at click time (imperative — no subscription): the dataset
    // filters, spatial scope, and the ValueRollup group-by / applied period.
    const filters = store.get(serverFiltersAtom);
    const mapScope = store.get(mapScopeAtom);
    const cashflowPeriod = store.get(rollupCashflowPeriodAtom);
    // Effective group-by = the persisted atom, else the options-catalog default (so the export
    // matches the on-screen table). Empty ⇒ omit config, letting the BE apply its own default.
    const dimensions =
      store.get(rollupDimensionsAtom) ??
      rollupOptions?.defaults.dimensions ??
      [];
    // The active bin (single value ⇒ mutually exclusive) rides in the aggregation config so
    // the exported table matches the screen. snake_case keys match the nested `dimensions`
    // key + the BE job's string-key reads.
    const bin = store.get(rollupBinAtom);
    const { numericBin, temporalBin } = rollupBinParams(bin);
    // Include the aggregation config when it's exported AND there's something to group by.
    // `rollupHasGrouping` (dimensions OR a numeric bin) matches the table's fetch + blank-state
    // gates, so the export can't diverge from the on-screen table (a temporal bin alone doesn't
    // group — it needs a dimension — so it won't ship a config the exporter can't run).
    const includeAggregationConfig =
      sections.includes('evaluation_output_aggregation') &&
      rollupHasGrouping(dimensions, bin);
    // Build config object: aggregation config + spatial config (when present)
    const config: ContentExportPayload['config'] = {};
    if (includeAggregationConfig) {
      config.evaluation_output_aggregation = {
        dimensions,
        ...(numericBin ? { numeric_bin: numericBin } : {}),
        ...(temporalBin ? { temporal_bin: temporalBin } : {}),
      };
    }
    // Per-table column presets (Properties / Property Allocations / Cashflows by Month). Every
    // checked preset-section ships its `column_preset` — each BE maps its own 'all_columns'
    // (master data → all row keys; cashflows → the "All Columns Format" preset). 'current_view'
    // also ships the table's shown columns in on-screen order, which both BEs keep (read from the
    // shared atoms — master-data keys vs cashflows' camelCase ids, underscored by the BE). An
    // empty/absent list (a table the user never opened, or one with every column hidden) is
    // omitted, so the BE falls back to all columns.
    const masterDataVisible = store.get(masterDataVisibleColumnsAtom);
    const cashflowsVisible = store.get(cashflowsVisibleColumnsAtom);
    const currentViewColumns = (
      section: ColumnPresetSection
    ): string[] | undefined => {
      const view = PRESET_SECTION_TO_VIEW[section];
      return (view ? masterDataVisible[view] : cashflowsVisible) ?? undefined;
    };
    COLUMN_PRESET_SECTIONS.forEach((section) => {
      if (!sections.includes(section)) {
        return;
      }
      const preset = columnPresets[section];
      const columns =
        preset === 'current_view' ? currentViewColumns(section) : undefined;
      config[section] = {
        column_preset: preset,
        ...(columns?.length ? { columns } : {}),
      };
    });
    if (sections.includes('spatial') && spatialConfig) {
      config.spatial = spatialConfig;
    }

    const payload: ContentExportPayload = {
      sections,
      ...(filters.length ? { filters } : {}),
      ...(cashflowPeriod ? { cashflowPeriod } : {}),
      ...(mapScope ? { mapScope } : {}),
      ...(Object.keys(config).length ? { config } : {}),
    };

    // Capture only the registered charts whose group is checked — pulled at click time, so it
    // reflects exactly what's mounted now (Charts & Visuals: the live rendered charts).
    const imageCaptures = getEntries()
      .filter((entry) => imageGroups.includes(entry.group))
      .map((entry) => ({ key: entry.id, exportToBlob: entry.exportToBlob }));

    start({ payload, imageCaptures, imageOptions });
  };

  // Success/failure toast for the export lifecycle — shared with the scenario-assumptions flow
  // (INS-12392). `onProcessing` frees the user from the modal on the 202; `failOnlyFromProcessing`
  // keeps a pre-202 (uploading → failed) failure on the modal's inline error instead of toasting.
  const toastNode = useExportLifecycleToast(
    status,
    error,
    {
      processing: 'Export started — your download will begin when it’s ready.',
      complete: 'Your export is ready.',
    },
    { onProcessing: () => setOpen(false), failOnlyFromProcessing: true }
  );

  return {
    open: openModal,
    isExporting,
    modalProps: {
      open,
      items: CONTENT_EXPORT_ITEMS,
      checkedIds,
      onToggleItem: toggleItem,
      disabledIds,
      isPackageMode,
      showImageOptions,
      imageOptions,
      onImageOptionsChange: setImageOptions,
      spatialConfig,
      onSpatialConfigChange: setSpatialConfig,
      columnPresets: { value: columnPresets, onChange: setColumnPreset },
      isExporting,
      // Inline error only for a pre-202 POST failure (modal still open, retry in place); a
      // background failure is toasted instead (the modal has already auto-closed).
      error: status === 'failed' ? error : null,
      onExport: handleExport,
      onClose: () => setOpen(false),
    },
    toast: toastNode,
  };
}
